import { getAssignmentsByTeacher } from './assignmentService'
import { getTeacherClasses } from './teacherClassService'
import { getStudentsByTeacherClass, getStudentsPaginated } from './userService'
import { getAttemptsByTeacherInRange } from './attemptService'
import { validateAnswerWithFunctions, DEGREE_TO_FUNCTIONS } from '../utils/riemannFunctions'
import { buildStudentAlerts, buildPedagogicalRecommendations } from '../utils/teacherRiskRules'
import { getPlatformDailyStats } from './statsService'

const sessionCache = new Map()

function toDateSafe(value) {
  if (!value) return null
  if (value.toDate) return value.toDate()
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function getPeriodDays(period) {
  if (period === '7d') return 7
  if (period === '90d') return 90
  if (period === 'trimester') return 120
  return 30
}

function buildDateWindow(period) {
  const end = new Date()
  const start = new Date(end)
  start.setDate(start.getDate() - getPeriodDays(period))
  return { start, end }
}

function median(values) {
  if (!Array.isArray(values) || values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 0) {
    return Math.round((sorted[mid - 1] + sorted[mid]) / 2)
  }
  return Math.round(sorted[mid])
}

function byDateKey(dateValue) {
  const d = toDateSafe(dateValue)
  if (!d) return null
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function buildDailySeries(attempts) {
  const bucket = new Map()
  attempts.forEach((a) => {
    const key = byDateKey(a.completedAt)
    if (!key) return
    const prev = bucket.get(key) || { date: key, scoreSum: 0, count: 0 }
    prev.scoreSum += Number(a.score || 0)
    prev.count += 1
    bucket.set(key, prev)
  })
  return Array.from(bucket.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((row) => ({
      date: row.date,
      participation: row.count,
      averageScore: row.count > 0 ? Math.round(row.scoreSum / row.count) : 0
    }))
}

function buildDailySeriesFromAggregates(rows) {
  return (rows || []).map((row) => ({
    date: row.date,
    participation: Number(row.totalAttempts || 0),
    averageScore: Number(row.averageScore || 0)
  }))
}

function buildScoreDistribution(attempts) {
  const ranges = [
    { key: '0-39', min: 0, max: 39, count: 0 },
    { key: '40-69', min: 40, max: 69, count: 0 },
    { key: '70-84', min: 70, max: 84, count: 0 },
    { key: '85-100', min: 85, max: 100, count: 0 }
  ]
  attempts.forEach((a) => {
    const score = Number(a.score || 0)
    const target = ranges.find((r) => score >= r.min && score <= r.max)
    if (target) target.count += 1
  })
  return ranges
}

function buildSkillsHeatmapRows(attempts) {
  const skills = new Map()
  const ensure = (key, label) => {
    if (!skills.has(key)) {
      skills.set(key, { key, label, total: 0, correct: 0, successRate: 0 })
    }
    return skills.get(key)
  }

  attempts.forEach((attempt) => {
    const correctAnswers = Array.isArray(attempt.correctAnswers) ? attempt.correctAnswers : []
    const userAnswers = Array.isArray(attempt.userAnswers) ? attempt.userAnswers : []

    correctAnswers.forEach((correct, index) => {
      if (!correct) return
      const userAnswer = userAnswers[index]
      const selectedFunction = userAnswer?.selectedFunction || userAnswer?.function || null
      const hasDegree = !!(userAnswer?.degree || userAnswer?.root || userAnswer?.displayLabel)
      const functionOnlyAvailable = !hasDegree && !!selectedFunction
      const validation = validateAnswerWithFunctions(userAnswer, correct, selectedFunction, { functionOnlyAvailable })
      const isCorrect = validation?.level === 1

      const degree = (correct.degree || '').toUpperCase().replace('°', '')
      if (degree && DEGREE_TO_FUNCTIONS[degree]?.[0]) {
        const funcCode = DEGREE_TO_FUNCTIONS[degree][0]
        const labels = { T: 'Fonction Tonique', SD: 'Fonction Sous-dominante', D: 'Fonction Dominante' }
        const row = ensure(`function-${funcCode}`, labels[funcCode] || `Fonction ${funcCode}`)
        row.total += 1
        if (isCorrect) row.correct += 1
      }

      const cadence = String(correct.cadence || '').trim()
      if (cadence) {
        const cadenceLabel = cadence.charAt(0).toUpperCase() + cadence.slice(1)
        const row = ensure(`cadence-${cadence}`, `Cadence ${cadenceLabel}`)
        row.total += 1
        if (isCorrect) row.correct += 1
      }

      if (degree) {
        const row = ensure(`degree-${degree}`, `Degre ${degree}`)
        row.total += 1
        if (isCorrect) row.correct += 1
      }
    })
  })

  return Array.from(skills.values())
    .map((row) => ({
      ...row,
      successRate: row.total > 0 ? Math.round((row.correct / row.total) * 100) : 0
    }))
    .sort((a, b) => b.total - a.total)
}

function buildStudentMetrics(students, attempts, assignmentStatsByStudent) {
  const attemptsByStudent = new Map()
  attempts.forEach((attempt) => {
    const list = attemptsByStudent.get(attempt.userId) || []
    list.push(attempt)
    attemptsByStudent.set(attempt.userId, list)
  })

  return students.map((student) => {
    const studentAttempts = (attemptsByStudent.get(student.id) || []).sort((a, b) => {
      const ta = toDateSafe(a.completedAt)?.getTime() || 0
      const tb = toDateSafe(b.completedAt)?.getTime() || 0
      return tb - ta
    })
    const scores = studentAttempts.map((a) => Number(a.score || 0))
    const scoreAvg = scores.length > 0 ? Math.round(scores.reduce((s, v) => s + v, 0) / scores.length) : 0
    const lateAssignments = assignmentStatsByStudent.get(student.id)?.lateAssignments || 0
    return {
      studentId: student.id,
      studentName: student.displayName || student.email || 'Eleve',
      classId: student.teacherClassId || null,
      establishment: student.establishment || '',
      studentClass: student.class || '',
      studentLevel: student.level || '',
      averageScore: scoreAvg,
      attemptCount: studentAttempts.length,
      lateAssignments,
      lastActivityAt: studentAttempts[0]?.completedAt || null,
      recentScores: scores
    }
  })
}

function buildAssignmentRows(assignments, classNames, studentsByClass, attempts) {
  const attemptsByAssignment = new Map()
  attempts.forEach((attempt) => {
    if (!attempt.assignmentId) return
    const list = attemptsByAssignment.get(attempt.assignmentId) || []
    list.push(attempt)
    attemptsByAssignment.set(attempt.assignmentId, list)
  })

  return assignments.map((assignment) => {
    const classStudents = studentsByClass.get(assignment.teacherClassId) || []
    const classSize = classStudents.length
    const classStudentIds = new Set(classStudents.map((s) => s.id))
    const relatedAttempts = (attemptsByAssignment.get(assignment.id) || [])
      .filter((a) => classStudentIds.has(a.userId))
    const latestByStudent = new Map()
    relatedAttempts.forEach((attempt) => {
      const currentTime = toDateSafe(attempt.completedAt)?.getTime() || 0
      const prev = latestByStudent.get(attempt.userId)
      const prevTime = toDateSafe(prev?.completedAt)?.getTime() || 0
      if (!prev || currentTime >= prevTime) latestByStudent.set(attempt.userId, attempt)
    })
    const submittedCount = latestByStudent.size
    const scores = Array.from(latestByStudent.values()).map((item) => Number(item.score || 0))
    const medianScore = median(scores)
    const dueDate = toDateSafe(assignment.dueDate)
    const overdueCount = dueDate && dueDate.getTime() < Date.now()
      ? Math.max(0, classSize - submittedCount)
      : 0
    const submissionRate = classSize > 0 ? Math.round((submittedCount / classSize) * 100) : 0
    return {
      assignmentId: assignment.id,
      exerciseId: assignment.exerciseId || assignment.items?.[0]?.exerciseId || null,
      exerciseThumbnail: assignment.exerciseThumbnail || assignment.items?.[0]?.exerciseThumbnail || null,
      title: assignment.title || assignment.exerciseTitle || 'Exercice',
      classId: assignment.teacherClassId,
      className: classNames.get(assignment.teacherClassId) || 'Classe',
      classSize,
      submittedCount,
      overdueCount,
      submissionRate,
      medianScore,
      dueDate
    }
  })
}

const EMPTY_SCORE_DISTRIBUTION = [
  { key: '0-39', min: 0, max: 39, count: 0 },
  { key: '40-69', min: 40, max: 69, count: 0 },
  { key: '70-84', min: 70, max: 84, count: 0 },
  { key: '85-100', min: 85, max: 100, count: 0 }
]

async function getAllStudentsLimited(maxCount = 300) {
  const pageSize = Math.min(100, maxCount)
  let startAfterDoc = null
  let students = []

  while (students.length < maxCount) {
    const { students: page, lastDoc } = await getStudentsPaginated({
      pageSize,
      startAfterDoc
    })
    students = [...students, ...(page || [])]
    if (!lastDoc || !page || page.length === 0) break
    startAfterDoc = lastDoc
  }

  return students.slice(0, maxCount)
}

export async function getTeacherInsights(teacherId, options = {}) {
  const {
    period = '30d',
    teacherClassId = '',
    scope = 'mine',
    isAdminTeacher = false
  } = options
  const effectiveScope = scope === 'all' && isAdminTeacher ? 'all' : 'mine'
  const cacheKey = `${teacherId}|${period}|${teacherClassId || 'all'}|${effectiveScope}`
  const cached = sessionCache.get(cacheKey)
  if (cached) return cached

  const { start, end } = buildDateWindow(period)
  const classes = await getTeacherClasses(teacherId)
  const assignments = effectiveScope === 'mine'
    ? await getAssignmentsByTeacher(teacherId, 80)
    : []

  const selectedClassIds = effectiveScope === 'mine'
    ? (teacherClassId
    ? [teacherClassId]
      : classes.map((c) => c.id))
    : []

  const classNames = new Map(classes.map((c) => [c.id, c.name]))
  const studentsByClassEntries = effectiveScope === 'mine'
    ? await Promise.all(
      selectedClassIds.map(async (classId) => [classId, await getStudentsByTeacherClass(classId)])
    )
    : []
  const studentsByClass = new Map(studentsByClassEntries)
  const allStudents = effectiveScope === 'mine'
    ? studentsByClassEntries.flatMap(([, list]) => list)
    : await getAllStudentsLimited(300)
  const fetchedAttempts = effectiveScope === 'mine'
    ? await getAttemptsByTeacherInRange(teacherId, {
      teacherClassId: teacherClassId || null,
      rosterClassIds: selectedClassIds,
      startDate: start,
      endDate: end,
      maxCount: 3000
    })
    : []
  const dailyAgg = effectiveScope === 'all'
    ? await getPlatformDailyStats(start, end)
    : { rows: [], totals: { totalAttempts: 0, totalScoreSum: 0, averageScore: 0 } }

  const activeRosterStudentIds = new Set(allStudents.map((student) => student.id))
  const attempts = effectiveScope === 'mine'
    ? fetchedAttempts.filter((attempt) => activeRosterStudentIds.has(attempt.userId))
    : fetchedAttempts

  const filteredAssignments = assignments.filter((a) =>
    !teacherClassId || a.teacherClassId === teacherClassId
  )
  const assignmentRows = buildAssignmentRows(filteredAssignments, classNames, studentsByClass, attempts)

  const lateByStudent = new Map()
  assignmentRows.forEach((row) => {
    if (!row.dueDate || row.overdueCount <= 0) return
    const classStudents = studentsByClass.get(row.classId) || []
    const assignmentAttempts = attempts.filter((a) => a.assignmentId === row.assignmentId)
    const submittedIds = new Set(assignmentAttempts.map((a) => a.userId))
    classStudents.forEach((student) => {
      if (!submittedIds.has(student.id)) {
        const prev = lateByStudent.get(student.id) || { lateAssignments: 0 }
        prev.lateAssignments += 1
        lateByStudent.set(student.id, prev)
      }
    })
  })

  const studentMetrics = effectiveScope === 'mine'
    ? buildStudentMetrics(allStudents, attempts, lateByStudent)
    : allStudents.map((student) => ({
      studentId: student.id,
      studentName: student.displayName || student.email || 'Eleve',
      classId: student.teacherClassId || null,
      className: student.class || 'Classe',
      establishment: student.establishment || '',
      studentClass: student.class || '',
      studentLevel: student.level || '',
      averageScore: 0,
      attemptCount: 0,
      lateAssignments: 0,
      lastActivityAt: null,
      recentScores: [],
      riskScore: 0,
      riskLabel: 'stable',
      alertReasons: []
    }))
  const studentsWithAlerts = effectiveScope === 'mine'
    ? buildStudentAlerts(studentMetrics).map((item) => ({
      ...item,
      className: classNames.get(item.classId) || 'Classe'
    }))
    : studentMetrics

  const scoreValues = attempts.map((a) => Number(a.score || 0))
  const totalClassSize = allStudents.length
  const totalSubmitted = assignmentRows.reduce((sum, row) => sum + row.submittedCount, 0)
  const totalExpected = assignmentRows.reduce((sum, row) => sum + row.classSize, 0)
  const totalOverdue = assignmentRows.reduce((sum, row) => sum + row.overdueCount, 0)
  const active7dCutoff = new Date()
  active7dCutoff.setDate(active7dCutoff.getDate() - 7)
  const activeStudentIds = new Set(
    attempts
      .filter((a) => (toDateSafe(a.completedAt)?.getTime() || 0) >= active7dCutoff.getTime())
      .map((a) => a.userId)
  )

  const skillsHeatmapRows = effectiveScope === 'mine' ? buildSkillsHeatmapRows(attempts) : []
  const weakSkills = skillsHeatmapRows
    .filter((row) => row.total >= 8 && row.successRate < 55)
    .sort((a, b) => a.successRate - b.successRate)
  const weakestAssignments = [...assignmentRows]
    .filter((row) => row.classSize > 0)
    .sort((a, b) => a.medianScore - b.medianScore)
    .map((row) => ({ ...row }))
  const recommendations = buildPedagogicalRecommendations({ weakSkills, weakestAssignments })
  const warnings = []
  if (effectiveScope === 'mine') {
    warnings.push('Les indicateurs detailles (retards, eleves a risque, devoirs) comptent uniquement les tentatives effectuees dans le cadre des devoirs.')
    warnings.push('Les scores et statistiques de classe ne comptent que les eleves actuellement rattaches a la classe.')
    if (allStudents.length > 0 && attempts.length === 0) {
      warnings.push('Des eleves sont rattaches a vos classes, mais aucune tentative de devoir n a ete detectee sur la periode selectionnee.')
    }
  } else {
    warnings.push('Vue globale admin basee sur des agregats journaliers pour limiter les lectures Firestore.')
  }
  const aggregatedMedianLikeScore = dailyAgg.totals.averageScore
  const dailySeries = effectiveScope === 'mine'
    ? buildDailySeries(attempts)
    : buildDailySeriesFromAggregates(dailyAgg.rows)

  const result = {
    meta: {
      period,
      startDate: start,
      endDate: end,
      classFilter: teacherClassId || null,
      scope: effectiveScope,
      warnings
    },
    filters: {
      classes: classes.map((c) => ({ id: c.id, name: c.name }))
    },
    kpis: {
      classCount: effectiveScope === 'mine' ? selectedClassIds.length : 0,
      studentCount: totalClassSize,
      submissionRate: effectiveScope === 'mine' ? (totalExpected > 0 ? Math.round((totalSubmitted / totalExpected) * 100) : 0) : 0,
      overdueCount: effectiveScope === 'mine' ? totalOverdue : 0,
      medianScore: effectiveScope === 'mine' ? median(scoreValues) : aggregatedMedianLikeScore,
      activeStudents7d: effectiveScope === 'mine' ? activeStudentIds.size : 0,
      atRiskCount: studentsWithAlerts.filter((s) => s.riskScore >= 40).length
    },
    charts: {
      daily: dailySeries.length > 0 ? dailySeries : buildDailySeries(attempts),
      scoreDistribution: effectiveScope === 'mine' ? buildScoreDistribution(attempts) : EMPTY_SCORE_DISTRIBUTION
    },
    lists: {
      atRiskStudents: studentsWithAlerts.filter((s) => s.riskScore >= 40),
      allStudents: studentsWithAlerts,
      assignments: effectiveScope === 'mine' ? assignmentRows.sort((a, b) => a.medianScore - b.medianScore) : [],
      recommendations: effectiveScope === 'mine' ? recommendations : []
    },
    skillsHeatmapRows,
    attempts
  }

  sessionCache.set(cacheKey, result)
  return result
}

export function clearTeacherInsightsCache() {
  sessionCache.clear()
}
